import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';

import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { DataState, Skeleton, deriveDataState } from '../loading';
import { PACKAGE_KIND_LABELS } from '../../services/packages';
import { getPackageVersions } from '../../services/packageVersions';
import { formatDisplayDate } from '../../utils/dateDisplay';

// What each listed version was (keepr-api KPR-1882). A `listing` keeps its
// version number: its words changed, and what it adds to a collection did not.
const CHANGE_WORDS = {
	publish: 'Listed on Discover',
	rebuild: 'What it adds changed',
	listing: 'Its name or description changed',
};

/** A row's second line: what changed and the day, or that nothing was kept of it. */
function changeLine(row) {
	if (!row.change) return 'Listed before keepr kept a version history';
	return [CHANGE_WORDS[row.change] || 'Changed', row.date ? formatDisplayDate(row.date) : null].filter(Boolean).join(' · ');
}

/**
 * The package page's Version history: the versions Discover has listed, newest
 * first, with the one listed now marked. Read when it opens. A package's
 * record starts at the review (keepr-api KPR-1277), so an older package says
 * that its earlier versions were not kept rather than seeming to start late.
 */
export default function PackageVersionHistory({ pkg, open, onClose }) {
	const [state, setState] = useState({ status: 'loading', rows: [], total: 0 });
	const [attempt, setAttempt] = useState(0);
	const key = pkg?.key;

	useEffect(() => {
		if (!open || !key) return undefined;
		let live = true;
		setState({ status: 'loading', rows: [], total: 0 });
		getPackageVersions(key)
			.then(({ rows, total }) => { if (live) setState({ status: 'ready', rows, total }); })
			.catch(() => { if (live) setState({ status: 'error', rows: [], total: 0 }); });
		return () => { live = false; };
	}, [open, key, attempt]);

	if (!pkg) return null;
	const { status, rows, total } = state;
	const isEmpty = status === 'ready' && rows.length === 0;
	const error = status === 'error' ? 'Check your connection and try again.' : null;
	const oldest = rows[rows.length - 1];
	const unkept = oldest && oldest.change && oldest.version > 1 && rows.length >= total ? oldest.version : null;
	const preview = Array.isArray(pkg.preview) ? pkg.preview : [];
	return (
		<Modal title="Version history" open={open} onCancel={onClose} footer={null} destroyOnHidden>
			<div className="modal-identity">
				<RecordIdentity
					kicker={PACKAGE_KIND_LABELS[pkg.kind] || 'Package'}
					badge={(
						<RecordBadge
							kind={pkg.kind === 'collection' ? 'collection' : 'card'}
							{...(pkg.kind === 'bundle' && preview.length > 1 ? { records: preview } : { record: { icon: pkg.icon, color: pkg.color, name: pkg.name } })}
							title={pkg.name}
						/>
					)}
				/>
			</div>
			<DataState
				state={deriveDataState({ loading: status === 'loading', error, hasData: rows.length > 0, isEmpty })}
				hasData={rows.length > 0}
				skeleton={<Skeleton variant="line" count={3} />}
				error={error}
				onRetry={() => setAttempt(n => n + 1)}
				label="the version history"
				isEmpty={isEmpty}
				empty={<div className="package-versions__empty">No versions to show.</div>}
			>
				<ol className="package-versions">
					{rows.map((row, index) => (
						<li key={`${row.version}:${row.change}:${index}`} className="package-versions__row">
							<div className="package-versions__head">
								<span className="package-versions__version">Version {row.version}</span>
								{row.current ? <span className="installed-badge" data-state="installed">Current</span> : null}
							</div>
							<div className="meta-line package-versions__change">{changeLine(row)}</div>
						</li>
					))}
				</ol>
				{unkept ? <p className="meta-line package-versions__unkept">keepr kept no record of versions before {unkept}.</p> : null}
			</DataState>
		</Modal>
	);
}
